import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styles from './ChartV2.module.scss';
import {
  DEFAULT_HORIZONS,
  DEFAULT_INTERVALS,
  DEFAULT_PANE_ID,
} from './constants';
import {
  measureYAxisColumnWidth,
  resolveThemeFromElement,
  Y_AXIS_COLUMN_WIDTH,
} from './engine/draw';
import { getYAxisTicks, yAxisTickCount } from './engine/nice-ticks';
import {
  DEFAULT_CHART_PALETTE,
  paletteColorAt,
  resolveCssVarColor,
} from './engine/palette';
import { computeYExtent } from './engine/scales';
import { useHorizonInterval } from './hooks/useHorizonInterval';
import { useLiveFeed } from './hooks/useLiveFeed';
import { usePaneUIState } from './hooks/usePaneUIState';
import { useSeriesMeta } from './hooks/useSeriesMeta';
import { useSeriesVisibility } from './hooks/useSeriesVisibility';
import { useSharedDomain } from './hooks/useSharedDomain';
import { PaneDivider } from './internals/PaneDivider';
import { PaneView } from './internals/PaneView';
import {
  ChartV2Handle,
  ChartV2Insight,
  ChartV2LegendEntry,
  ChartV2PriceDisplay,
  ChartV2Props,
  ChartV2ResolvedSeries,
  ChartV2SeriesType,
} from './types/chart-v2.types';
import { buildDefaultLegendMenu, seriesHasOhlc } from './ui/legend-menu';
import { ViewController } from './ui/ViewController';
import {
  computeDataDisabledHorizons,
  computeDataExtent,
  computeInitialDomain,
} from './utils/data-extent';
import { formatYAxisTick, yAxisTickDecimals } from './utils/format';
import { groupByPane, type Pane, seriesPaneId } from './utils/panes';
import { themeToCssVars } from './utils/theme-vars';

/**
 * Measure the y-axis column width needed to fit every pane's widest tick
 * label, floored at `Y_AXIS_COLUMN_WIDTH`. Shared across panes so plot
 * widths — and therefore the x-axis / crosshair — stay aligned. Uses the
 * same tick generator and label formatter as the canvas axis.
 */
/** Typical plot height when measuring axis labels before layout. */
const AXIS_LABEL_MEASURE_PLOT_HEIGHT = 280;

const computeAxisColumnWidth = (
  el: HTMLElement | null,
  panes: Pane<ChartV2ResolvedSeries>[],
  sharedDomain: [number, number]
): number => {
  if (!el) {
    return Y_AXIS_COLUMN_WIDTH;
  }

  const { fontMono } = resolveThemeFromElement(el);
  const labels: string[] = [];
  const tickCount = yAxisTickCount(AXIS_LABEL_MEASURE_PLOT_HEIGHT);

  for (const pane of panes) {
    for (const s of pane.series) {
      const extent = computeYExtent([s], sharedDomain);
      const ticks = getYAxisTicks([extent.min, extent.max], tickCount);
      const decimals = yAxisTickDecimals(ticks);

      for (const t of ticks) {
        labels.push(formatYAxisTick(t, decimals));
      }
    }
  }

  return measureYAxisColumnWidth(fontMono, labels);
};

/**
 * Canvas-driven, standalone chart component. Composes a top bar
 * (`ViewController`), one or more panes (`ChartCanvas`), an optional
 * legend, and live-tick wiring into a single drop-in chart.
 *
 * Required: `series`. Most other props (horizon, interval, palette,
 * insights, callbacks) are optional with sensible defaults — pass them
 * to control the chart from the host or to observe interactions.
 *
 * For the raw drawing surface without the chrome, use `ChartCanvas`.
 * For the top-bar selectors only, use `ViewController`.
 */
export const ChartV2 = (props: ChartV2Props) => {
  const {
    ref,
    series,
    horizon: controlledHorizon,
    interval: controlledInterval,
    defaultHorizon = '1Y',
    defaultInterval = '1D',
    availableHorizons = DEFAULT_HORIZONS,
    availableIntervals = DEFAULT_INTERVALS,
    disabledHorizons,
    autoDisableHorizonsByData = true,
    enabledIntervals,
    showIntervalSelector = true,
    showViewController = true,
    enableZoom = true,
    hoverVariant = 'default',
    topBarActions,
    defaultHiddenSeriesIds,
    defaultPriceDisplayByPane,
    theme,
    locales,
    className,
    viewControllerClassName,
    renderLegend,
    renderLegendTooltip,
    showDefaultLegend = true,
    onHorizonChange,
    onIntervalChange,
    onSeriesTypeChange,
    onAllSeriesTypeChange,
    onSeriesColorChange,
    onDomainChange,
    onHoverChange,
    onSeriesToggle,
    onSeriesMovePane,
    onPaneMove,
    onPaneCollapse,
    showCollapse = false,
    showMaximize = false,
    onPaneMaximize,
    onPaneRemove,
    onClearAll,
    onRemoveSeries,
    colors = DEFAULT_CHART_PALETTE,
    liveFeed,
    insights,
  } = props;

  const defaultPriceDisplayKey = useMemo(
    () => JSON.stringify(defaultPriceDisplayByPane ?? {}),
    [defaultPriceDisplayByPane]
  );
  const defaultPriceDisplayState = useMemo(
    () => defaultPriceDisplayByPane ?? {},
    [defaultPriceDisplayKey]
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const firstCanvasRef = useRef<ChartV2Handle>(null);

  // Horizon / interval — controlled when props given, uncontrolled otherwise.
  const { horizon, interval, handleHorizonChange, handleIntervalChange } =
    useHorizonInterval({
      controlledHorizon,
      controlledInterval,
      defaultHorizon,
      defaultInterval,
      onHorizonChange,
      onIntervalChange,
    });

  // Visibility — hide / remove tracking.
  const {
    hiddenSeriesIds,
    removedSeriesIds,
    handleSeriesToggle,
    removeSeriesIds,
  } = useSeriesVisibility({ defaultHiddenSeriesIds, onSeriesToggle });

  // Per-series meta (color / seriesType / paneIndex) + move handlers.
  const {
    seriesMeta,
    handleSeriesColorChange,
    handlePerSeriesTypeChange,
    handleAllSeriesTypeChange,
    handleSeriesMovePane,
    handlePaneMove,
  } = useSeriesMeta({
    series,
    onSeriesColorChange,
    onSeriesTypeChange,
    onAllSeriesTypeChange,
    onSeriesMovePane,
    onPaneMove,
  });

  // Streaming live feed.
  const liveBySeries = useLiveFeed(liveFeed);

  // Bump on theme change so palette-dependent memos re-resolve CSS vars.
  const [paletteTick, setPaletteTick] = useState(0);

  useLayoutEffect(() => {
    setPaletteTick(t => t + 1);
  }, [theme]);

  const themeStyle = useMemo(() => themeToCssVars(theme), [theme]);
  const rootClassName = [styles.root, className].filter(Boolean).join(' ');

  // Effective series — layer meta + live updates on top of caller's props.
  // Palette identity comes from `colors` (often CSS vars); paint colour is
  // resolved for canvas. Same identity is what ColorPalette selects against.
  const effectiveSeries = useMemo<ChartV2ResolvedSeries[]>(() => {
    // Palette index is taken from the caller's series order, not the
    // post-filter position — otherwise removing a series re-indexes the
    // survivors onto different swatches.
    return series
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => !removedSeriesIds.has(s.id))
      .map(({ s, i }) => {
        const meta = seriesMeta[s.id];
        const live = liveBySeries[s.id];
        const data =
          live && live.points.length > 0 ? [...s.data, ...live.points] : s.data;
        const colorKey = meta?.color ?? s.color ?? paletteColorAt(colors, i);
        const resolvedColor =
          resolveCssVarColor(rootRef.current, colorKey) ?? colorKey;
        const resolvedType: ChartV2SeriesType =
          meta?.seriesType ?? s.seriesType;
        const paneIndex = meta?.paneIndex ?? 0;

        return {
          ...s,
          color: resolvedColor,
          colorKey,
          seriesType: resolvedType,
          paneId: s.paneId ?? `pane-${paneIndex}`,
          data,
        };
      });
  }, [series, seriesMeta, liveBySeries, paletteTick, removedSeriesIds, colors]);

  // Include hidden series so hide-all keeps horizon auto-disable / reset
  // domain on the real loaded span (v1). Drawing still uses plottedPanes.
  const dataExtent = useMemo(
    () => computeDataExtent(effectiveSeries),
    [effectiveSeries]
  );
  const effectiveDisabledHorizons = useMemo(() => {
    if (!autoDisableHorizonsByData) {
      return disabledHorizons;
    }

    const next = new Set([
      ...computeDataDisabledHorizons(dataExtent, availableHorizons),
      ...(disabledHorizons ?? []),
    ]);

    return Array.from(next);
  }, [
    dataExtent,
    availableHorizons,
    disabledHorizons,
    autoDisableHorizonsByData,
  ]);
  const initialDomain = useMemo(
    () => computeInitialDomain(horizon, dataExtent, enableZoom),
    [horizon, dataExtent, enableZoom]
  );

  const { sharedDomain, handleDomainChange, handleDomainReset } =
    useSharedDomain({
      horizon,
      dataExtent,
      initialDomain,
      onDomainChange,
    });

  const [sharedHoverTime, setSharedHoverTime] = useState<number | null>(null);
  // Per-pane price-display mode (`price` | `indexed`). Internal state
  // only — consumers don't need to wire this to succeed, but we could
  // expose a callback in the future for URL persistence.
  const [priceDisplayByPane, setPriceDisplayByPane] = useState<
    Record<string, ChartV2PriceDisplay>
  >(() => defaultPriceDisplayState);

  useEffect(() => {
    setPriceDisplayByPane(defaultPriceDisplayState);
  }, [defaultPriceDisplayState]);

  const setPaneDisplay = useCallback(
    (paneId: string, next: ChartV2PriceDisplay) => {
      setPriceDisplayByPane(prev => ({ ...prev, [paneId]: next }));
    },
    []
  );

  const handleResetChart = useCallback(() => {
    handleDomainReset();
    setPriceDisplayByPane(defaultPriceDisplayState);
  }, [handleDomainReset, defaultPriceDisplayState]);

  // Include hidden series so hide keeps the pane + its legend row (v1).
  const panes = useMemo(() => groupByPane(effectiveSeries), [effectiveSeries]);

  // The same panes minus the hidden series — what each pane actually draws,
  // and so what the axis gutter is sized for. Index-aligned with `panes`.
  const plottedPanes = useMemo(
    () =>
      panes.map(pane => ({
        ...pane,
        series: pane.series.filter(s => !hiddenSeriesIds.has(s.id)),
      })),
    [panes, hiddenSeriesIds]
  );

  // Shared y-axis column count *and* width across panes so plot widths match
  // (keeps the x-axis / crosshair aligned). Width fits the widest tick label
  // so long values aren't clipped; `paletteTick` re-resolves the font on mount.
  const { count: sharedAxisColumnCount, width: axisColumnWidth } = useMemo(
    () => ({
      count: plottedPanes.reduce((max, pane) => {
        const effective =
          priceDisplayByPane[pane.id] === 'indexed' ? 1 : pane.series.length;

        return Math.max(max, effective);
      }, 1),
      width: computeAxisColumnWidth(
        rootRef.current,
        plottedPanes,
        sharedDomain
      ),
    }),
    [plottedPanes, priceDisplayByPane, sharedDomain, paletteTick]
  );

  const {
    paneFractions,
    collapsedPanes,
    maximizedPaneId,
    toggleCollapsed,
    toggleMaximized,
    clearCollapsedFor,
    setMaximizedIfMatch,
    startDividerDrag,
  } = usePaneUIState({
    paneCount: panes.length,
    bodyRef,
    onPaneCollapse,
    onPaneMaximize,
  });

  useImperativeHandle(
    ref,
    () => ({
      resetZoom: handleDomainReset,
      setDomain: range => handleDomainChange(range),
      exportImage: () => firstCanvasRef.current?.exportImage(),
    }),
    [handleDomainChange, handleDomainReset]
  );

  const handlePaneRemove = useCallback(
    (paneId: string) => {
      const victims = effectiveSeries
        .filter(s => seriesPaneId(s) === paneId)
        .map(s => s.id);

      removeSeriesIds(victims);

      if (onRemoveSeries) {
        victims.forEach(id => onRemoveSeries(id));
      }

      clearCollapsedFor(paneId);
      setMaximizedIfMatch(paneId);
      onPaneRemove?.(paneId);
    },
    [
      effectiveSeries,
      removeSeriesIds,
      onRemoveSeries,
      clearCollapsedFor,
      setMaximizedIfMatch,
      onPaneRemove,
    ]
  );

  // Pane index lookup for the more-menu move-up/down enablement.
  const paneIdsOrder = useMemo(() => panes.map(p => p.id), [panes]);
  const lastPaneIndex = paneIdsOrder.length - 1;

  const buildEntry = useCallback(
    (s: ChartV2ResolvedSeries): ChartV2LegendEntry => {
      const paneId = s.paneId ?? DEFAULT_PANE_ID;
      const paneOrderIndex = paneIdsOrder.indexOf(paneId);
      const sameInPane = effectiveSeries.filter(
        other => seriesPaneId(other) === paneId
      ).length;
      const canMoveUp = paneOrderIndex > 0;
      const canMoveDown = paneOrderIndex < lastPaneIndex || sameInPane > 1;

      return {
        series: s,
        hidden: hiddenSeriesIds.has(s.id),
        moreMenuItems: buildDefaultLegendMenu({
          seriesId: s.id,
          color: s.colorKey,
          seriesType: s.seriesType,
          hasOhlc: seriesHasOhlc(s),
          canMoveUp,
          canMoveDown,
          colors,
          onColorChange: (color, colorIndex) =>
            handleSeriesColorChange(s.id, color, colorIndex),
          onSeriesTypeChange: type => handlePerSeriesTypeChange(s.id, type),
          onMovePane: direction => handleSeriesMovePane(s.id, direction),
          labels: {
            changeColor: locales?.legendMenuChangeColor,
            moveUp: locales?.legendMenuMoveUp,
            moveDown: locales?.legendMenuMoveDown,
            seriesTypeLabel: {
              line: locales?.legendMenuSeriesTypeLine,
              area: locales?.legendMenuSeriesTypeArea,
              candlestick: locales?.legendMenuSeriesTypeCandlestick,
              bar: locales?.legendMenuSeriesTypeBar,
            },
          },
        }),
      };
    },
    [
      effectiveSeries,
      hiddenSeriesIds,
      paneIdsOrder,
      lastPaneIndex,
      colors,
      handleSeriesColorChange,
      handlePerSeriesTypeChange,
      handleSeriesMovePane,
    ]
  );

  const insightsByPane = useMemo(() => {
    const map = new Map<string, ChartV2Insight[]>();

    if (!insights || insights.length === 0) {
      return map;
    }

    const primaryPaneId = panes[0]?.id;

    for (const insight of insights) {
      const paneId = insight.paneId ?? primaryPaneId;

      if (!paneId) {
        continue;
      }

      const bucket = map.get(paneId);

      if (bucket) {
        bucket.push(insight);
        continue;
      }

      map.set(paneId, [insight]);
    }

    return map;
  }, [insights, panes]);

  const legendEntriesByPane = useMemo(() => {
    const map = new Map<string, ChartV2LegendEntry[]>();

    for (const pane of panes) {
      map.set(pane.id, pane.series.map(buildEntry));
    }

    return map;
  }, [panes, buildEntry]);

  const allLegendEntries = useMemo<ChartV2LegendEntry[]>(
    () => effectiveSeries.map(buildEntry),
    [effectiveSeries, buildEntry]
  );

  const hasContent = effectiveSeries.some(s => s.data.length > 0);
  const hasOhlc = effectiveSeries.some(s => s.data.some(p => 'close' in p));
  const disabledSeriesTypes: ChartV2SeriesType[] = hasOhlc
    ? []
    : ['candlestick', 'bar'];

  // `selected` feeds the chip bar's "currently active" highlight. With
  // per-series overrides possible, derive it from the first series'
  // resolved type (what the primary asset is actually rendered as) so
  // the chip stays in sync with reality.
  const primarySeriesType: ChartV2SeriesType =
    effectiveSeries[0]?.seriesType ?? 'line';

  // Per-series items feed the chart-type dropdown when more than one
  // series is plotted — each row gets its own toggle so the user can
  // change types for a specific asset without touching the others.
  const chartTypeSeriesItems = useMemo(
    () =>
      effectiveSeries.map(s => ({
        id: s.id,
        label: s.legendLabel ?? s.label,
        seriesType: s.seriesType,
        disabled: !seriesHasOhlc(s),
      })),
    [effectiveSeries]
  );

  return (
    <div
      ref={rootRef}
      className={rootClassName}
      style={themeStyle}
      data-testid="chartv2-root"
    >
      {showViewController && (
        <ViewController
          className={viewControllerClassName}
          horizons={availableHorizons}
          selectedHorizon={horizon}
          onHorizonChange={handleHorizonChange}
          disabledHorizons={effectiveDisabledHorizons}
          intervals={availableIntervals}
          selectedInterval={interval}
          onIntervalChange={handleIntervalChange}
          enabledIntervals={enabledIntervals}
          showIntervalSelector={showIntervalSelector}
          topBarActions={topBarActions}
          selectedSeriesType={primarySeriesType}
          onSeriesTypeChange={handleAllSeriesTypeChange}
          disabledSeriesTypes={disabledSeriesTypes}
          chartTypeSeriesItems={chartTypeSeriesItems}
          onPerSeriesTypeChange={handlePerSeriesTypeChange}
          locales={locales}
          onClearAll={hasContent ? onClearAll : undefined}
          clearAllLabel={locales?.clearAll}
          // Match the v1 chart's empty/initial-load behaviour: the
          // horizon, interval and chart-type controls are inert until
          // at least one series has datapoints. Prevents users from
          // tweaking them before the first data frame lands and getting
          // confused by a "selection" that doesn't render anything.
          disabled={!hasContent}
        />
      )}
      <div
        className={styles.body}
        data-chart-body
        data-testid="chartv2-body"
        ref={bodyRef}
      >
        <div className={styles.panes}>
          {panes.map((pane, idx) => {
            const isHiddenByMaximize =
              maximizedPaneId !== null && maximizedPaneId !== pane.id;

            if (isHiddenByMaximize) {
              return <React.Fragment key={pane.id} />;
            }

            const isCollapsed = collapsedPanes.has(pane.id);
            const isMaximized = maximizedPaneId === pane.id;
            const isLast = idx === panes.length - 1;
            const fraction = isCollapsed
              ? 0
              : (paneFractions[idx] ?? 1 / panes.length);
            const isXAxisPane = maximizedPaneId ? isMaximized : isLast;
            const canShowDivider = !isLast && !isMaximized;
            const paneEntries = legendEntriesByPane.get(pane.id) ?? [];

            return (
              <React.Fragment key={pane.id}>
                <PaneView
                  pane={pane}
                  drawnSeries={plottedPanes[idx].series}
                  index={idx}
                  axisColumnCount={sharedAxisColumnCount}
                  axisColumnWidth={axisColumnWidth}
                  paneCount={panes.length}
                  fraction={fraction}
                  isCollapsed={isCollapsed}
                  isMaximized={isMaximized}
                  isXAxisPane={isXAxisPane}
                  paneEntries={paneEntries}
                  allLegendEntries={allLegendEntries}
                  renderLegend={renderLegend}
                  renderLegendTooltip={renderLegendTooltip}
                  showDefaultLegend={showDefaultLegend}
                  locales={locales}
                  sharedDomain={sharedDomain}
                  dataExtent={dataExtent}
                  sharedHoverTime={sharedHoverTime}
                  showLatestPrice={!!liveFeed}
                  hoverVariant={hoverVariant}
                  enableZoom={enableZoom}
                  insights={insightsByPane.get(pane.id)}
                  canvasRef={firstCanvasRef}
                  priceDisplay={priceDisplayByPane[pane.id] ?? 'price'}
                  onPriceDisplayChange={next => setPaneDisplay(pane.id, next)}
                  onResetChart={handleResetChart}
                  onHoverTimeChange={setSharedHoverTime}
                  onHoverChange={idx === 0 ? onHoverChange : undefined}
                  onDomainChange={handleDomainChange}
                  onSeriesToggle={handleSeriesToggle}
                  onRemoveSeries={onRemoveSeries}
                  onPaneMoveUp={() =>
                    handlePaneMove(pane.id, 'up', effectiveSeries)
                  }
                  onPaneMoveDown={() =>
                    handlePaneMove(pane.id, 'down', effectiveSeries)
                  }
                  onPaneCollapse={
                    showCollapse ? () => toggleCollapsed(pane.id) : undefined
                  }
                  onPaneMaximize={
                    showMaximize ? () => toggleMaximized(pane.id) : undefined
                  }
                  onPaneRemove={() => handlePaneRemove(pane.id)}
                />
                {canShowDivider && (
                  <PaneDivider index={idx} onPointerDown={startDividerDrag} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};
